import { Button, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { Notification } from 'notifications/types'
import React, { useMemo, useState } from 'react'
import { FlatList, View } from 'react-native'
import { ParsedTextProps } from 'react-native-parsed-text'
import { SafeAreaView } from 'react-native-safe-area-context'
import RefreshControl from 'shared/components/refresh_control'
import { NotificationItem } from './item'

interface Props {
  notifications: Notification[] | undefined
  onPressNotification: (notification: Notification) => void
  onPressMarkAllAsRead: () => void
  onNextPageNeeded: () => void
  onRefresh: () => Promise<void>
  onLinkPress: (url: string) => void
  isUnreadNotification: boolean
}

export default function NotificationsView({
  notifications,
  onPressNotification,
  onPressMarkAllAsRead,
  onNextPageNeeded,
  onRefresh,
  isUnreadNotification,
  onLinkPress,
}: Props) {
  const styles = useStyles()
  const [refreshing, setRefreshing] = useState(false)
  const { theme } = useTheme()

  const parsePatterns: ParsedTextProps['parse'] = useMemo(() => {
    const patterns: ParsedTextProps['parse'] = [
      {
        style: { textDecorationLine: 'underline', color: theme.colors.primary },
        pattern: /(https?:\/\/|www\.)[^\s\]]+/gi,
        onPress: (text: string) => {
          onLinkPress(text)
        },
      },
    ]
    return patterns
  }, [onLinkPress, theme.colors.primary])

  if (!notifications) {
    return null
  }

  if (notifications.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.emptyMessage}>{t('noNotifications')}</Text>
      </View>
    )
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <FlatList
          keyExtractor={notification => notification.id}
          data={notifications}
          renderItem={({ item }) => (
            <NotificationItem
              notification={item}
              onPressNotification={onPressNotification}
              onLinkPress={onLinkPress}
              parsePatterns={parsePatterns}
            />
          )}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={async () => {
                setRefreshing(true)
                await onRefresh()
                setRefreshing(false)
              }}
            />
          }
          onEndReached={onNextPageNeeded}
          onEndReachedThreshold={0.3}
          // ------------- //
          // Workaround for scroll bar bug: https://github.com/facebook/react-native/issues/26610
          scrollIndicatorInsets={{ right: Number.MIN_VALUE }}
          // ------------- //
        />
        <View style={styles.floatingView}>
          <Button
            title={t('markAllRead')}
            onPress={onPressMarkAllAsRead}
            buttonStyle={styles.button}
            disabled={!isUnreadNotification}
          />
        </View>
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  emptyMessage: {
    alignSelf: 'center',
    paddingTop: 300,
    fontSize: 17,
  },
  button: {
    padding: 10,
    margin: 5,
    borderRadius: 10,
    opacity: 0.8,
  },
  floatingView: {
    position: 'absolute',
    bottom: 30,
    right: 16,
  },
}))
